import { useEffect, useState } from 'react';
import { IDrawerModalAnimationOrigin } from '../../components/CareMapDrawerPage';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { TextField } from '../../components/TextField';
import { CareMapMemberAvatar } from '../../avatar/Avatars';
import { AvatarIdentityFactory } from '../../avatar/AvatarIdentityFactory';
import { DateField } from '../../components/DateField';
import { IContactMethod } from '../../caremap/ICareMap';
import { EditPatientAvatarDrawer } from '../../avatar/EditAvatarDrawer';
import { PatientFormState } from '../../avatar/PatientFormState';
import { EmailField, IEmailContactMethod } from '../../contact-methods/EmailField';
import { IPhoneContactMethod, PhoneField } from '../../contact-methods/PhoneField';
import { LinkedMemberEmailField } from '../../caremap/LinkedMemberEmailField';

export const ActivePatientEditForm: React.FC = () => {
    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const [showingAvatarPicker, setIsShowingAvatarPicker] = useState(false);
    const [avatarDrawerAnimationOrigin, setAvatarDrawerAnimationOrigin] = useState<IDrawerModalAnimationOrigin>();
    const avatarClicked = (e: React.MouseEvent) => {
        setAvatarDrawerAnimationOrigin({x: e.clientX, y: e.clientY});
        setIsShowingAvatarPicker(true);
    };

    const patientForm = useInstance(PatientFormState);
    const avatarIdentities = useInstance(AvatarIdentityFactory);
    useSubscription(() => patientForm);

    const patient = patientForm.state.patient;

    useEffect(() => {
        const caremap = careMapState.getActiveCaremap();
        if (caremap){
            patientForm.init(caremap.patient);
        }
        return () => {
            patientForm.clear();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [Boolean(careMapState.state), patientForm]);

    if (!careMapState.state || !patient) {
        return null;
    }

    const contactMethodChanged = (method: IContactMethod) => {
        const copy = [ ...patient.contactMethods ];
        const idx = copy.findIndex(m => m.type === method.type);

        if (idx > -1) {
            copy.splice(idx, 1, method);
        } else {
            copy.push(method);
        }

        patientForm.patch({ contactMethods: copy });
    };

    // Unlink: clear the email contact method and persist via the normal save path so the server
    // drops linkedUserId. Also clear linkedUserId optimistically so the form immediately flips
    // back to the editable EmailField rather than waiting for the post-save refresh.
    const onUnlink = () => {
        const contactMethods = patient.contactMethods.filter(m => m.type !== 'email');
        patientForm.patch({ contactMethods, linkedUserId: undefined });
        careMapState.updatePatient({ ...patient, contactMethods, linkedUserId: undefined });
    };

    return (
        <>
            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>

                <div style={{display: 'flex', alignItems: 'start', width: '100%', gap: 20}}>
                    <CareMapMemberAvatar
                        // the form's patient, so an avatar just picked shows before it is saved
                        identity={avatarIdentities.forMember(patient)}
                        showsStoredAvatar
                        onClick={(e: React.MouseEvent) => avatarClicked(e)}
                    />

                    <div style={{display: 'flex', flexDirection: 'column', gap: 20, flexGrow: 1}}>
                        <TextField
                            name="first-name"
                            label="First Name"
                            value={patient.firstName}
                            autocapitalize="words"
                            onInput={firstName => patientForm.patch({ firstName })}
                        />
                        <TextField
                            name="last-name"
                            label="Last Name"
                            value={patient.lastName}
                            autocapitalize="words"
                            onInput={lastName => patientForm.patch({ lastName })}
                        />
                    </div>
                </div>

                <DateField label="Date of Birth" data-type="dob" value={patient.dob} onChange={dob => patientForm.patch({ dob })} />

                <TextField
                    name="address"
                    label="Address"
                    value={patient.address}
                    autocapitalize="words"
                    onInput={address => patientForm.patch({ address })}
                />

                {patient.linkedUserId ? (
                    <LinkedMemberEmailField
                        email={careMapState.getContactMethodOrDefault(patient, 'email').value}
                        linkedToName={patient.firstName || 'this person'}
                        onUnlink={onUnlink}
                        isSelf={careMapState.isMyMember(patient)}
                    />
                ) : (
                    <EmailField
                        methodChanged={contactMethodChanged}
                        method={careMapState.getContactMethodOrDefault(patient, 'email') as IEmailContactMethod}
                    />
                )}
                <PhoneField
                    methodChanged={contactMethodChanged}
                    method={careMapState.getContactMethodOrDefault(patient, 'mobile-phone') as IPhoneContactMethod}
                />
                <PhoneField
                    methodChanged={contactMethodChanged}
                    method={careMapState.getContactMethodOrDefault(patient, 'work-phone') as IPhoneContactMethod}
                />

            </div>
            <EditPatientAvatarDrawer
                isOpen={showingAvatarPicker}
                animationOrigin={avatarDrawerAnimationOrigin}
                patient={patient}
                onSave={changed => {
                    patientForm.patch(changed);
                    setIsShowingAvatarPicker(false);
                }}
                onCancel={() => setIsShowingAvatarPicker(false)}
            />
        </>
    );
};
